(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  const scrubThreshold = 6;
  // Desktop hover shows the second image as a CSS fade, so pointer scrubbing is off.
  const fadeOnHover = true;

  class TraptCardGallery extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.previousButton = this.querySelector('[data-gallery-previous]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.activeIndex = 0;
      this.targetIndex = 0;
      this.scrollFrame = null;
      this.scrubFrame = null;
      this.entryX = 0;
      this.pointerX = 0;
      this.scrubReady = false;

      if (!this.track || this.slides.length < 2) return;

      this.track.addEventListener('scroll', () => this.queueActiveUpdate(), { passive: true });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event));
      this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
      this.addEventListener('mouseenter', (event) => this.startScrub(event));
      this.addEventListener('mousemove', (event) => this.queueScrub(event));
      this.addEventListener('mouseleave', () => this.stopScrub());
    }

    disconnectedCallback() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.scrubFrame) cancelAnimationFrame(this.scrubFrame);
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.goTo(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }

    goTo(index, behavior = reduceMotion.matches ? 'auto' : 'smooth') {
      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const nextSlide = this.slides[nextIndex];
      if (!nextSlide) return;
      // Track the destination separately from scroll progress so mousemove
      // does not restart a smooth scroll on every animation frame.
      this.targetIndex = nextIndex;
      this.track.scrollTo({ left: nextSlide.offsetLeft, behavior });
      this.setActive(nextIndex);
    }

    queueActiveUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const slideWidth = this.track.clientWidth || 1;
        this.setActive(Math.round(this.track.scrollLeft / slideWidth));
      });
    }

    setActive(index) {
      this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex === this.slides.length - 1;
    }

    startScrub(event) {
      if (fadeOnHover || !hoverCapable.matches) return;
      this.entryX = event.clientX;
      this.pointerX = event.clientX;
      this.scrubReady = false;
      this.goTo(1);
    }

    queueScrub(event) {
      if (fadeOnHover || !hoverCapable.matches) return;
      if (event.target.closest('[data-gallery-previous], [data-gallery-next]')) return;

      this.pointerX = event.clientX;
      if (!this.scrubReady) {
        if (Math.abs(this.pointerX - this.entryX) < scrubThreshold) return;
        this.scrubReady = true;
      }

      if (this.scrubFrame) return;
      this.scrubFrame = requestAnimationFrame(() => {
        this.scrubFrame = null;
        const bounds = this.getBoundingClientRect();
        if (!bounds.width) return;
        const position = Math.max(0, Math.min(.999999, (this.pointerX - bounds.left) / bounds.width));
        const nextIndex = Math.floor(position * this.slides.length);
        if (nextIndex !== this.targetIndex) this.goTo(nextIndex);
      });
    }

    stopScrub() {
      if (fadeOnHover || !hoverCapable.matches) return;
      if (this.scrubFrame) {
        cancelAnimationFrame(this.scrubFrame);
        this.scrubFrame = null;
      }
      this.scrubReady = false;
      this.goTo(0);
    }
  }

  if (!customElements.get('trapt-card-gallery')) {
    customElements.define('trapt-card-gallery', TraptCardGallery);
  }
})();
